import React, { useState, useEffect } from 'react';
import * as pc from 'playcanvas';
import { useNavigate, useLocation } from 'react-router-dom';
import NumberInput from './NumberInput';
import { defaultValue } from '../common/config';

const AdminPanel = ({ onGalleryFileChange, onCarFileChange, lights, galleryEnabled, setGalleryEnabled, galleryEntity, carEntity, shadowCasterEntity, original=false, bgConfig, hideAdminPanel, modelConfig}) => {

    const DEFAULT_VALUES = {
        directionalIntensity: defaultValue.light.directional.directionalConfig.intensity,
        pointIntensity: defaultValue.light.point.pointConfig.intensity,
        pointRange: defaultValue.light.point.pointConfig.range,
        directionalEnabled: defaultValue.light.directional.directionalConfig.enabled,
        pointEnabled: defaultValue.light.point.pointConfig.enabled,
        ambientEnabled: defaultValue.light.ambient.ambientConfig.enabled,
        directionalShadowsEnabled: defaultValue.light.directional.directionalConfig.castShadows,
        directionalShadowIntensity: defaultValue.light.directional.directionalConfig.shadowIntensity,
        directionalEulerX: defaultValue.light.directional.eulerAngles.x,
        directionalEulerY: defaultValue.light.directional.eulerAngles.y,
        directionalEulerZ: defaultValue.light.directional.eulerAngles.z,
        pointPositionX: defaultValue.light.point.localPosition.x,
        pointPositionY: defaultValue.light.point.localPosition.y,
        pointPositionZ: defaultValue.light.point.localPosition.z,
        carPositionX: defaultValue.car.localPosition.x + modelConfig.position.x,
        carPositionY: defaultValue.car.localPosition.y + modelConfig.position.y,
        carPositionZ: defaultValue.car.localPosition.z + modelConfig.position.z,
        carEulerX: defaultValue.car.eulerAngles.x + modelConfig.rotation.x,
        carEulerY: defaultValue.car.eulerAngles.y + modelConfig.rotation.y,
        carEulerZ: defaultValue.car.eulerAngles.z + modelConfig.rotation.z,
        carScaleX: defaultValue.car.localScale.x * modelConfig.scale_factor,
        carScaleY: defaultValue.car.localScale.y * modelConfig.scale_factor,
        carScaleZ: defaultValue.car.localScale.z * modelConfig.scale_factor,
        shadowPositionX: defaultValue.shadow_caster.localPosition.x + modelConfig.position.x,
        shadowPositionY: defaultValue.shadow_caster.localPosition.y + modelConfig.position.y,
        shadowPositionZ: defaultValue.shadow_caster.localPosition.z + modelConfig.position.z,
        shadowEulerX: defaultValue.shadow_caster.eulerAngles.x + modelConfig.rotation.x,
        shadowEulerY: defaultValue.shadow_caster.eulerAngles.y + modelConfig.rotation.y,
        shadowEulerZ: defaultValue.shadow_caster.eulerAngles.z + modelConfig.rotation.z,
        shadowScaleX: defaultValue.shadow_caster.localScale.x * modelConfig.scale_factor,
        shadowScaleY: defaultValue.shadow_caster.localScale.y * modelConfig.scale_factor,
        shadowScaleZ: defaultValue.shadow_caster.localScale.z * modelConfig.scale_factor,
    };

    const navigate = useNavigate();
    const location = useLocation();
    const bgIdFromParams = new URLSearchParams(location.search).get('bg_id');

    const [isExpanded, setIsExpanded] = useState(false);
    const [galleryFileName, setGalleryFileName] = useState('');
    const [carFileName, setCarFileName] = useState('');
    const [selectedGalleryFile, setSelectedGalleryFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    
    // Light control states
    const [directionalEnabled, setDirectionalEnabled] = useState(DEFAULT_VALUES.directionalEnabled);
    const [pointEnabled, setPointEnabled] = useState(DEFAULT_VALUES.pointEnabled);
    const [ambientEnabled, setAmbientEnabled] = useState(DEFAULT_VALUES.ambientEnabled);
    const [directionalShadowsEnabled, setDirectionalShadowsEnabled] = useState(DEFAULT_VALUES.directionalShadowsEnabled);
    const [directionalShadowIntensity, setDirectionalShadowIntensity] = useState(DEFAULT_VALUES.directionalShadowIntensity);
    const [directionalIntensity, setDirectionalIntensity] = useState(DEFAULT_VALUES.directionalIntensity);
    const [pointIntensity, setPointIntensity] = useState(DEFAULT_VALUES.pointIntensity);
    const [pointRange, setPointRange] = useState(DEFAULT_VALUES.pointRange);
    const [directionalEulerX, setDirectionalEulerX] = useState(DEFAULT_VALUES.directionalEulerX);
    const [directionalEulerY, setDirectionalEulerY] = useState(DEFAULT_VALUES.directionalEulerY);
    const [directionalEulerZ, setDirectionalEulerZ] = useState(DEFAULT_VALUES.directionalEulerZ);
    const [pointPositionX, setPointPositionX] = useState(DEFAULT_VALUES.pointPositionX);
    const [pointPositionY, setPointPositionY] = useState(DEFAULT_VALUES.pointPositionY);
    const [pointPositionZ, setPointPositionZ] = useState(DEFAULT_VALUES.pointPositionZ);

    // Save Configuration states
    const [name, setName] = useState('');
    const [type, setType] = useState('');
    const [isActive, setIsActive] = useState("false");
    const [source, setSource] = useState('web');
    const [bgUrl, setBgUrl] = useState('');
    const [bgId, setBgId] = useState('');
    const [presignedUrl, setPresignedUrl] = useState('');
    const [apiConfigData, setApiConfigData] = useState(null);
    const [cameraPosition, setCameraPosition] = useState({ x: 0, y: 0, z: 0 });
    const [cameraRotation, setCameraRotation] = useState({ x: 0, y: 0, z: 0 });

    const [carPositionX, setCarPositionX] = useState(DEFAULT_VALUES.carPositionX);
    const [carPositionY, setCarPositionY] = useState(DEFAULT_VALUES.carPositionY);
    const [carPositionZ, setCarPositionZ] = useState(DEFAULT_VALUES.carPositionZ);
    const [carEulerX, setCarEulerX] = useState(DEFAULT_VALUES.carEulerX);
    const [carEulerY, setCarEulerY] = useState(DEFAULT_VALUES.carEulerY);
    const [carEulerZ, setCarEulerZ] = useState(DEFAULT_VALUES.carEulerZ);
    const [carScaleX, setCarScaleX] = useState(DEFAULT_VALUES.carScaleX);
    const [carScaleY, setCarScaleY] = useState(DEFAULT_VALUES.carScaleY);
    const [carScaleZ, setCarScaleZ] = useState(DEFAULT_VALUES.carScaleZ);
    const [shadowPositionX, setShadowPositionX] = useState(DEFAULT_VALUES.shadowPositionX);
    const [shadowPositionY, setShadowPositionY] = useState(DEFAULT_VALUES.shadowPositionY);
    const [shadowPositionZ, setShadowPositionZ] = useState(DEFAULT_VALUES.shadowPositionZ);
    const [shadowEulerX, setShadowEulerX] = useState(DEFAULT_VALUES.shadowEulerX);
    const [shadowEulerY, setShadowEulerY] = useState(DEFAULT_VALUES.shadowEulerY);
    const [shadowEulerZ, setShadowEulerZ] = useState(DEFAULT_VALUES.shadowEulerZ);
    const [shadowScaleX, setShadowScaleX] = useState(DEFAULT_VALUES.shadowScaleX);
    const [shadowScaleY, setShadowScaleY] = useState(DEFAULT_VALUES.shadowScaleY);
    const [shadowScaleZ, setShadowScaleZ] = useState(DEFAULT_VALUES.shadowScaleZ);

    // Add this new useEffect to update camera position
    useEffect(() => {
        if (lights?.app?.systems?.camera?.cameras[0]) {
            const camera = lights.app.systems.camera.cameras[0];
            
            // Function to update camera position and rotation
            const updateCameraInfo = () => {
                const pos = camera.entity.getPosition();
                const rot = camera.entity.getEulerAngles();
                
                setCameraPosition({
                    x: parseFloat(pos.x.toFixed(2)),
                    y: parseFloat(pos.y.toFixed(2)),
                    z: parseFloat(pos.z.toFixed(2))
                });
                
                setCameraRotation({
                    x: parseFloat(rot.x.toFixed(2)),
                    y: parseFloat(rot.y.toFixed(2)),
                    z: parseFloat(rot.z.toFixed(2))
                });
            };

            // Update every frame
            lights.app.on('update', updateCameraInfo);

            // Cleanup
            return () => {
                lights.app.off('update', updateCameraInfo);
            };
        }
    }, [lights]);

    const uploadToS3 = async (presignedUrl, file) => {
        try {
            const response = await fetch(presignedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': 'application/octet-stream'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to upload file to S3');
            }
        } catch (error) {
            console.error('Error uploading to S3:', error);
            throw error;
        }
    };

    // Add this new useEffect
    useEffect(() => {
        const initializeConfig = () => {
            if (bgIdFromParams && bgConfig?.light) {
                try {
                    const apiConfig = bgConfig
                    if (apiConfig?.light) {
                        // Update all the states with API config values
                        setApiConfigData(apiConfig);
                        setDirectionalEnabled(apiConfig.light.directional.directionalConfig.enabled); // or whatever default you want
                        setDirectionalIntensity(apiConfig.light.directional.directionalConfig.intensity);
                        setDirectionalShadowIntensity(apiConfig.light.directional.directionalConfig.shadowIntensity);
                        setPointIntensity(apiConfig.light.point.pointConfig.intensity);
                        setPointRange(apiConfig.light.point.pointConfig.range);
                        setPointEnabled(apiConfig.light.point.pointConfig.enabled);
                        setAmbientEnabled(apiConfig.light.ambient.ambientConfig.enabled);
                        setDirectionalShadowsEnabled(apiConfig.light.directional.directionalConfig.castShadows);
                        setDirectionalEulerX(apiConfig.light.directional.eulerAngles.x);
                        setDirectionalEulerY(apiConfig.light.directional.eulerAngles.y);
                        setDirectionalEulerZ(apiConfig.light.directional.eulerAngles.z);
                        setPointPositionX(apiConfig.light.point.localPosition.x);
                        setPointPositionY(apiConfig.light.point.localPosition.y);
                        setPointPositionZ(apiConfig.light.point.localPosition.z);

                        // Also update the lights if they exist
                        if (lights?.directionalLight) {
                            lights.directionalLight.light.enabled = apiConfig.light.directional.directionalConfig.enabled;
                            lights.directionalLight.light.intensity = apiConfig.light.directional.directionalConfig.intensity;
                            lights.directionalLight.light.castShadows = apiConfig.light.directional.directionalConfig.castShadows;
                            lights.directionalLight.light.shadowIntensity = apiConfig.light.directional.directionalConfig.shadowIntensity;
                            lights.directionalLight.setEulerAngles(
                                apiConfig.light.directional.eulerAngles.x,
                                apiConfig.light.directional.eulerAngles.y,
                                apiConfig.light.directional.eulerAngles.z
                            );
                        }
                        if (lights?.pointLight) {
                            lights.pointLight.light.enabled = apiConfig.light.point.pointConfig.enabled;
                            lights.pointLight.light.intensity = apiConfig.light.point.pointConfig.intensity;
                            lights.pointLight.light.range = apiConfig.light.point.pointConfig.range;
                            lights.pointLight.setLocalPosition(
                                apiConfig.light.point.localPosition.x,
                                apiConfig.light.point.localPosition.y,
                                apiConfig.light.point.localPosition.z
                            );
                        }
                        if (lights?.app) {
                            lights.app.scene.ambientLight = apiConfig.light.ambient.ambientConfig.enabled ?
                                new pc.Color(1, 1, 1) : new pc.Color(0, 0, 0);
                        }
                    }
                    // Initialize car transform if available
                    if (apiConfig.car) {
                        setCarPositionX(apiConfig.car.localPosition.x);
                        setCarPositionY(apiConfig.car.localPosition.y);
                        setCarPositionZ(apiConfig.car.localPosition.z);
                        setCarEulerX(apiConfig.car.eulerAngles.x);
                        setCarEulerY(apiConfig.car.eulerAngles.y);
                        setCarEulerZ(apiConfig.car.eulerAngles.z);
                        setCarScaleX(apiConfig.car.localScale.x);
                        setCarScaleY(apiConfig.car.localScale.y);
                        setCarScaleZ(apiConfig.car.localScale.z);

                        if (carEntity) {
                            carEntity.setLocalPosition(
                                apiConfig.car.localPosition.x,
                                apiConfig.car.localPosition.y,
                                apiConfig.car.localPosition.z
                            );
                            carEntity.setEulerAngles(
                                apiConfig.car.eulerAngles.x,
                                apiConfig.car.eulerAngles.y,
                                apiConfig.car.eulerAngles.z
                            );
                            carEntity.setLocalScale(
                                apiConfig.car.localScale.x,
                                apiConfig.car.localScale.y,
                                apiConfig.car.localScale.z
                            );
                        }
                    }

                    if (apiConfig?.shadowCaster) {
                        setShadowPositionX(apiConfig.shadowCaster.localPosition.x);
                        setShadowPositionY(apiConfig.shadowCaster.localPosition.y);
                        setShadowPositionZ(apiConfig.shadowCaster.localPosition.z);
                        setShadowEulerX(apiConfig.shadowCaster.eulerAngles.x);
                        setShadowEulerY(apiConfig.shadowCaster.eulerAngles.y);
                        setShadowEulerZ(apiConfig.shadowCaster.eulerAngles.z);
                        setShadowScaleX(apiConfig.shadowCaster.localScale.x);
                        setShadowScaleY(apiConfig.shadowCaster.localScale.y);
                        setShadowScaleZ(apiConfig.shadowCaster.localScale.z);
                    }

                    if (shadowCasterEntity) {
                        shadowCasterEntity.setLocalPosition(
                            apiConfig.shadowCaster.localPosition.x,
                            apiConfig.shadowCaster.localPosition.y,
                            apiConfig.shadowCaster.localPosition.z
                        );
                        shadowCasterEntity.setEulerAngles(
                            apiConfig.shadowCaster.eulerAngles.x,
                            apiConfig.shadowCaster.eulerAngles.y,
                            apiConfig.shadowCaster.eulerAngles.z
                        );
                        shadowCasterEntity.setLocalScale(
                            apiConfig.shadowCaster.localScale.x,
                            apiConfig.shadowCaster.localScale.y,
                            apiConfig.shadowCaster.localScale.z
                        );
                    }
                } catch (error) {
                    console.error('Error initializing config:', error);
                }
            }
        };

        initializeConfig();
    }, [bgIdFromParams, lights, bgConfig]);

    const updateUrlParams = (newBgId) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('bg_id', newBgId);
        const newSearch = searchParams.toString();
        const newPath = `${location.pathname}?${newSearch}`;
        navigate(newPath, { replace: true });
    };

    const handleGalleryFileChange = (file) => {
        setGalleryFileName(file.name);
        setSelectedGalleryFile(file);
        onGalleryFileChange(file);
    };

    const handleCarFileChange = (file) => {
        setCarFileName(file.name);
        onCarFileChange(file);
    };

    const handleReset = () => {
        // If we have API config and bgIdFromParams, reset to API values
        if (apiConfigData?.light && bgIdFromParams) {
            setDirectionalEnabled(apiConfigData.light.directional.directionalConfig.enabled);
            setDirectionalIntensity(apiConfigData.light.directional.directionalConfig.intensity);
            setPointIntensity(apiConfigData.light.point.pointConfig.intensity);
            setPointRange(apiConfigData.light.point.pointConfig.range);
            setPointEnabled(apiConfigData.light.point.pointConfig.enabled);
            setAmbientEnabled(apiConfigData.light.ambient.ambientConfig.enabled);
            setDirectionalShadowsEnabled(apiConfigData.light.directional.directionalConfig.castShadows);
            setDirectionalEulerX(apiConfigData.light.directional.eulerAngles.x);
            setDirectionalEulerY(apiConfigData.light.directional.eulerAngles.y);
            setDirectionalEulerZ(apiConfigData.light.directional.eulerAngles.z);
            setPointPositionX(apiConfigData.light.point.localPosition.x);
            setPointPositionY(apiConfigData.light.point.localPosition.y);
            setPointPositionZ(apiConfigData.light.point.localPosition.z);
            setDirectionalShadowIntensity(apiConfigData.light.directional.directionalConfig.shadowIntensity);

            // Update lights with API values
            if (lights?.directionalLight) {
                lights.directionalLight.light.enabled = true;
                lights.directionalLight.light.intensity = apiConfigData.light.directional.directionalConfig.intensity;
                lights.directionalLight.light.shadowIntensity = apiConfigData.light.directional.directionalConfig.shadowIntensity;
                lights.directionalLight.light.castShadows = apiConfigData.light.directional.directionalConfig.castShadows;
                lights.directionalLight.setEulerAngles(
                    apiConfigData.light.directional.eulerAngles.x,
                    apiConfigData.light.directional.eulerAngles.y,
                    apiConfigData.light.directional.eulerAngles.z
                );
            }
            if (lights?.pointLight) {
                lights.pointLight.light.enabled = apiConfigData.light.point.pointConfig.enabled;
                lights.pointLight.light.intensity = apiConfigData.light.point.pointConfig.intensity;
                lights.pointLight.light.range = apiConfigData.light.point.pointConfig.range;
                lights.pointLight.setLocalPosition(
                    apiConfigData.light.point.localPosition.x,
                    apiConfigData.light.point.localPosition.y,
                    apiConfigData.light.point.localPosition.z
                );
            }
            if (lights?.app) {
                lights.app.scene.ambientLight = apiConfigData.light.ambient.ambientConfig.enabled ?
                    new pc.Color(1, 1, 1) : new pc.Color(0, 0, 0);
            }

            // Reset car values to API config if available
            if (apiConfigData.car) {
                setCarPositionX(apiConfigData.car.localPosition.x);
                setCarPositionY(apiConfigData.car.localPosition.y);
                setCarPositionZ(apiConfigData.car.localPosition.z);
                setCarEulerX(apiConfigData.car.eulerAngles.x);
                setCarEulerY(apiConfigData.car.eulerAngles.y);
                setCarEulerZ(apiConfigData.car.eulerAngles.z);
                setCarScaleX(apiConfigData.car.localScale.x);
                setCarScaleY(apiConfigData.car.localScale.y);
                setCarScaleZ(apiConfigData.car.localScale.z);

                if (carEntity) {
                    carEntity.setLocalPosition(
                        apiConfigData.car.localPosition.x,
                        apiConfigData.car.localPosition.y,
                        apiConfigData.car.localPosition.z
                    );
                    carEntity.setEulerAngles(
                        apiConfigData.car.eulerAngles.x,
                        apiConfigData.car.eulerAngles.y,
                        apiConfigData.car.eulerAngles.z
                    );
                    carEntity.setLocalScale(
                        apiConfigData.car.localScale.x,
                        apiConfigData.car.localScale.y,
                        apiConfigData.car.localScale.z
                    );
                }
            }
            if (apiConfigData?.shadowCaster) {
                // Reset to API values
                setShadowPositionX(apiConfigData.shadowCaster.localPosition.x);
                setShadowPositionY(apiConfigData.shadowCaster.localPosition.y);
                setShadowPositionZ(apiConfigData.shadowCaster.localPosition.z);
                setShadowEulerX(apiConfigData.shadowCaster.eulerAngles.x);
                setShadowEulerY(apiConfigData.shadowCaster.eulerAngles.y);
                setShadowEulerZ(apiConfigData.shadowCaster.eulerAngles.z);
                setShadowScaleX(apiConfigData.shadowCaster.localScale.x);
                setShadowScaleY(apiConfigData.shadowCaster.localScale.y);
                setShadowScaleZ(apiConfigData.shadowCaster.localScale.z);
    
                if (shadowCasterEntity) {
                    shadowCasterEntity.setLocalPosition(
                        apiConfigData.shadowCaster.localPosition.x,
                        apiConfigData.shadowCaster.localPosition.y,
                        apiConfigData.shadowCaster.localPosition.z
                    );
                    shadowCasterEntity.setEulerAngles(
                        apiConfigData.shadowCaster.eulerAngles.x,
                        apiConfigData.shadowCaster.eulerAngles.y,
                        apiConfigData.shadowCaster.eulerAngles.z
                    );
                    shadowCasterEntity.setLocalScale(
                        apiConfigData.shadowCaster.localScale.x,
                        apiConfigData.shadowCaster.localScale.y,
                        apiConfigData.shadowCaster.localScale.z
                    );
                }
            }
        } else {
            setDirectionalEnabled(DEFAULT_VALUES.directionalEnabled);
            setPointEnabled(DEFAULT_VALUES.pointEnabled);
            setAmbientEnabled(DEFAULT_VALUES.ambientEnabled);
            setDirectionalShadowsEnabled(DEFAULT_VALUES.directionalShadowsEnabled);
            setDirectionalIntensity(DEFAULT_VALUES.directionalIntensity);
            setPointIntensity(DEFAULT_VALUES.pointIntensity);
            setPointRange(DEFAULT_VALUES.pointRange);
            setDirectionalEulerX(DEFAULT_VALUES.directionalEulerX);
            setDirectionalEulerY(DEFAULT_VALUES.directionalEulerY);
            setDirectionalEulerZ(DEFAULT_VALUES.directionalEulerZ);
            setPointPositionX(DEFAULT_VALUES.pointPositionX);
            setPointPositionY(DEFAULT_VALUES.pointPositionY);
            setPointPositionZ(DEFAULT_VALUES.pointPositionZ);
            setDirectionalShadowIntensity(DEFAULT_VALUES.directionalShadowIntensity);

            if (lights?.directionalLight) {
                lights.directionalLight.light.enabled = DEFAULT_VALUES.directionalEnabled;
                lights.directionalLight.light.intensity = DEFAULT_VALUES.directionalIntensity;
                lights.directionalLight.light.shadowIntensity = DEFAULT_VALUES.directionalShadowIntensity;
                lights.directionalLight.light.castShadows = DEFAULT_VALUES.directionalShadowsEnabled;
                lights.directionalLight.setEulerAngles(
                    DEFAULT_VALUES.directionalEulerX,
                    DEFAULT_VALUES.directionalEulerY,
                    DEFAULT_VALUES.directionalEulerZ
                );
            }
            if (lights?.pointLight) {
                lights.pointLight.light.enabled = DEFAULT_VALUES.pointEnabled;
                lights.pointLight.light.intensity = DEFAULT_VALUES.pointIntensity;
                lights.pointLight.light.range = DEFAULT_VALUES.pointRange;
                lights.pointLight.setLocalPosition(
                    DEFAULT_VALUES.pointPositionX,
                    DEFAULT_VALUES.pointPositionY,
                    DEFAULT_VALUES.pointPositionZ
                );
            }
            if (lights?.app) {
                lights.app.scene.ambientLight = DEFAULT_VALUES.ambientEnabled ?
                    new pc.Color(1, 1, 1) : new pc.Color(0, 0, 0);
            }
        
            // Reset car values to default values
            setCarPositionX(DEFAULT_VALUES.carPositionX);
            setCarPositionY(DEFAULT_VALUES.carPositionY);
            setCarPositionZ(DEFAULT_VALUES.carPositionZ);
            setCarEulerX(DEFAULT_VALUES.carEulerX);
            setCarEulerY(DEFAULT_VALUES.carEulerY);
            setCarEulerZ(DEFAULT_VALUES.carEulerZ);
            setCarScaleX(DEFAULT_VALUES.carScaleX);
            setCarScaleY(DEFAULT_VALUES.carScaleY);
            setCarScaleZ(DEFAULT_VALUES.carScaleZ);

            if (carEntity) {
                carEntity.setLocalPosition(
                    DEFAULT_VALUES.carPositionX,
                    DEFAULT_VALUES.carPositionY,
                    DEFAULT_VALUES.carPositionZ
                );
                carEntity.setEulerAngles(
                    DEFAULT_VALUES.carEulerX,
                    DEFAULT_VALUES.carEulerY,
                    DEFAULT_VALUES.carEulerZ
                );
                carEntity.setLocalScale(
                    DEFAULT_VALUES.carScaleX,
                    DEFAULT_VALUES.carScaleY,
                    DEFAULT_VALUES.carScaleZ
                );
            }

            setShadowPositionX(DEFAULT_VALUES.shadowPositionX);
            setShadowPositionY(DEFAULT_VALUES.shadowPositionY);
            setShadowPositionZ(DEFAULT_VALUES.shadowPositionZ);
            setShadowEulerX(DEFAULT_VALUES.shadowEulerX);
            setShadowEulerY(DEFAULT_VALUES.shadowEulerY);
            setShadowEulerZ(DEFAULT_VALUES.shadowEulerZ);
            setShadowScaleX(DEFAULT_VALUES.shadowScaleX);
            setShadowScaleY(DEFAULT_VALUES.shadowScaleY);
            setShadowScaleZ(DEFAULT_VALUES.shadowScaleZ);

            if (shadowCasterEntity) {
                shadowCasterEntity.setLocalPosition(
                    DEFAULT_VALUES.shadowPositionX,
                    DEFAULT_VALUES.shadowPositionY,
                    DEFAULT_VALUES.shadowPositionZ
                );
                shadowCasterEntity.setEulerAngles(
                    DEFAULT_VALUES.shadowEulerX,
                    DEFAULT_VALUES.shadowEulerY,
                    DEFAULT_VALUES.shadowEulerZ
                );
                shadowCasterEntity.setLocalScale(
                    DEFAULT_VALUES.shadowScaleX,
                    DEFAULT_VALUES.shadowScaleY,
                    DEFAULT_VALUES.shadowScaleZ
                );
            }
        }
    };

    const getBgConfigDetails = () => {
        return {
            light: {
                directional: {
                    localPosition: {
                        x: 0.6,
                        y: 10,
                        z: 6.7
                    },
                    eulerAngles: {
                        x: directionalEulerX,
                        y: directionalEulerY,
                        z: directionalEulerZ
                    },
                    directionalConfig: {
                        type: 'directional',
                        color: new pc.Color(1, 1, 1),
                        intensity: directionalIntensity,
                        castShadows: directionalShadowsEnabled,
                        shadowDistance: 500,
                        shadowResolution: 2048,
                        normalOffsetBias: 0.05,
                        numCascades: 2,
                        shadowIntensity: 2,
                        shadowType: pc.SHADOW_VSM32,
                        vsmBlurMode: pc.BLUR_BOX,
                        vsmBias: 0.2,
                        affectSpecularity: false,
                        enabled: directionalEnabled,
                        shadowIntensity: directionalShadowIntensity,
                    }
                },
                point: {
                    localPosition: {
                        x: pointPositionX,
                        y: pointPositionY,
                        z: pointPositionZ
                    },
                    eulerAngles: {
                        x: 0,
                        y: 0,
                        z: 0
                    },
                    pointConfig: {
                        type: 'point',
                        color: new pc.Color(1, 1, 1),
                        intensity: pointIntensity,
                        range: pointRange,
                        enabled: pointEnabled
                    }
                },
                ambient: {
                    ambientConfig: {
                        enabled: ambientEnabled
                    }
                }
            },
            car: {
                localPosition: {
                    x: carPositionX,
                    y: carPositionY,
                    z: carPositionZ
                },
                eulerAngles: {
                    x: carEulerX,
                    y: carEulerY,
                    z: carEulerZ
                },
                localScale: {
                    x: carScaleX,
                    y: carScaleY,
                    z: carScaleZ
                }
            },
            shadowCaster: {
                localPosition: {
                    x: shadowPositionX,
                    y: shadowPositionY,
                    z: shadowPositionZ
                },
                eulerAngles: {
                    x: shadowEulerX,
                    y: shadowEulerY,
                    z: shadowEulerZ
                },
                localScale: {
                    x: shadowScaleX,
                    y: shadowScaleY,
                    z: shadowScaleZ
                }
            }
        }
    }

    const saveBgConfig = async () => {
        setIsSaving(true);
        const requestData = {
            name,
            type,
            is_active: false,
            source,
            metadata: {
                config: getBgConfigDetails()
            }
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_BASEURL_API_SPYNE}/3d/api/v1/bg/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();
            setBgId(data?.data?.bg_id);
            setPresignedUrl(data?.data?.bg_url);
            
            if (data?.data?.bg_url && selectedGalleryFile) {
                await uploadToS3(data.data.bg_url, selectedGalleryFile);
                alert("Background config and file uploaded successfully");
                updateUrlParams(data?.data?.bg_id)
            } else {
                alert("Please fill details");
            };
            window.location.reload();
        } catch (error) {
            console.error('Error saving background config:', error);
            alert("Error saving configuration");
        } finally {
            setIsSaving(false);
        }
    };

    const updateBgConfig = async (bgId) => {
        setIsSaving(true);
        const requestData = {
            bg_id: bgId,
            data: {
                metadata: {
                    config: getBgConfigDetails()
                }
            }
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_BASEURL_API_SPYNE}/3d/api/v1/bg/update`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            await response.json();
            alert("Background config updated successfully");
            window.location.reload();
        } catch (error) {
            console.error('Error updating background config:', error);
            alert("Error updating configuration");
        } finally {
            setIsSaving(false);
        }
    };


    // Light control handlers
    const handleDirectionalToggle = () => {
        setDirectionalEnabled(!directionalEnabled);
        if (lights?.directionalLight) {
            lights.directionalLight.light.enabled = !directionalEnabled;
        }
    };

    const handlePointToggle = () => {
        setPointEnabled(!pointEnabled);
        if (lights?.pointLight) {
            lights.pointLight.light.enabled = !pointEnabled;
        }
    };

    const handleAmbientToggle = () => {
        setAmbientEnabled(!ambientEnabled);
        if (lights?.app) {
            lights.app.scene.ambientLight = ambientEnabled ? 
                new pc.Color(0, 0, 0) : new pc.Color(1, 1, 1);
        }
    };

    const handleDirectionalShadowsToggle = () => {
        setDirectionalShadowsEnabled(!directionalShadowsEnabled);
        if (lights?.directionalLight) {
            lights.directionalLight.light.castShadows = !directionalShadowsEnabled;
        }
    };

    const handleDirectionalIntensityChange = (e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && value >= 0 && value <= 10) {
            setDirectionalIntensity(value);
            if (lights?.directionalLight) {
                lights.directionalLight.light.intensity = value;
            }
        }
    };

    const handlePointIntensityChange = (e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && value >= 0 && value <= 10) {
            setPointIntensity(value);
            if (lights?.pointLight) {
                lights.pointLight.light.intensity = value;
            }
        }
    };

    const handlePointRangeChange = (e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && value >= 0 && value <= 200) {
            setPointRange(value);
            if (lights?.pointLight) {
                lights.pointLight.light.range = value;
            }
        }
    };

    const handleDirectionalEulerChange = (axis, e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            switch(axis) {
                case 'x':
                    setDirectionalEulerX(value);
                    break;
                case 'y':
                    setDirectionalEulerY(value);
                    break;
                case 'z':
                    setDirectionalEulerZ(value);
                    break;
                default:
                    break;
            }
            if (lights?.directionalLight) {
                lights.directionalLight.setEulerAngles(
                    axis === 'x' ? value : directionalEulerX,
                    axis === 'y' ? value : directionalEulerY,
                    axis === 'z' ? value : directionalEulerZ
                );
            }
        }
    };

    const handlePointPositionChange = (axis, e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            switch(axis) {
                case 'x':
                    setPointPositionX(value);
                    break;
                case 'y':
                    setPointPositionY(value);
                    break;
                case 'z':
                    setPointPositionZ(value);
                    break;
                default:
                    break;
            }
            if (lights?.pointLight) {
                lights.pointLight.setLocalPosition(
                    axis === 'x' ? value : pointPositionX,
                    axis === 'y' ? value : pointPositionY,
                    axis === 'z' ? value : pointPositionZ
                );
            }
        }
    };

    const handleGalleryToggle = () => {
        setGalleryEnabled(!galleryEnabled);
        if (galleryEntity) {
            galleryEntity.enabled = !galleryEnabled;
        }
    };

      // Add handlers for car controls
      const handleCarPositionChange = (axis, e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            switch(axis) {
                case 'x':
                    setCarPositionX(value);
                    break;
                case 'y':
                    setCarPositionY(value);
                    break;
                case 'z':
                    setCarPositionZ(value);
                    break;
                default:
                    break;
            }
            if (carEntity) {
                carEntity.setLocalPosition(
                    axis === 'x' ? value : carPositionX,
                    axis === 'y' ? value : carPositionY,
                    axis === 'z' ? value : carPositionZ
                );
            }
        }
    };

    const handleCarEulerChange = (axis, e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            switch(axis) {
                case 'x':
                    setCarEulerX(value);
                    break;
                case 'y':
                    setCarEulerY(value);
                    break;
                case 'z':
                    setCarEulerZ(value);
                    break;
                default:
                    break;
            }
            if (carEntity) {
                carEntity.setEulerAngles(
                    axis === 'x' ? value : carEulerX,
                    axis === 'y' ? value : carEulerY,
                    axis === 'z' ? value : carEulerZ
                );
            }
        }
    };

    const handleCarScaleChange = (axis, e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && value > 0) {
            switch(axis) {
                case 'x':
                    setCarScaleX(value);
                    break;
                case 'y':
                    setCarScaleY(value);
                    break;
                case 'z':
                    setCarScaleZ(value);
                    break;
                default:
                    break;
            }
            if (carEntity) {
                carEntity.setLocalScale(
                    axis === 'x' ? value : carScaleX,
                    axis === 'y' ? value : carScaleY,
                    axis === 'z' ? value : carScaleZ
                );
            }
        }
    };

    const handleShadowPositionChange = (axis, e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            switch(axis) {
                case 'x':
                    setShadowPositionX(value);
                    break;
                case 'y':
                    setShadowPositionY(value);
                    break;
                case 'z':
                    setShadowPositionZ(value);
                    break;
                default:
                    break;
            }
            if (shadowCasterEntity) {
                shadowCasterEntity.setLocalPosition(
                    axis === 'x' ? value : shadowPositionX,
                    axis === 'y' ? value : shadowPositionY,
                    axis === 'z' ? value : shadowPositionZ
                );
            }
        }
    };

    const handleShadowEulerChange = (axis, e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value)) {
            switch(axis) {
                case 'x':
                    setShadowEulerX(value);
                    break;
                case 'y':
                    setShadowEulerY(value);
                    break;
                case 'z':
                    setShadowEulerZ(value);
                    break;
                default:
                    break;
            }
            if (shadowCasterEntity) {
                shadowCasterEntity.setEulerAngles(
                    axis === 'x' ? value : shadowEulerX,
                    axis === 'y' ? value : shadowEulerY,
                    axis === 'z' ? value : shadowEulerZ
                );
            }
        }
    };

    const handleShadowScaleChange = (axis, e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && value > 0) {
            switch(axis) {
                case 'x':
                    setShadowScaleX(value);
                    break;
                case 'y':
                    setShadowScaleY(value);
                    break;
                case 'z':
                    setShadowScaleZ(value);
                    break;
                default:
                    break;
            }
            if (shadowCasterEntity) {
                shadowCasterEntity.setLocalScale(
                    axis === 'x' ? value : shadowScaleX,
                    axis === 'y' ? value : shadowScaleY,
                    axis === 'z' ? value : shadowScaleZ
                );
            }
        }
    };

    const handleDirectionalShadowIntensityChange = (e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && value >= 0 && value <= 10) {
            setDirectionalShadowIntensity(value);
            if (lights?.directionalLight) {
                lights.directionalLight.light.shadowIntensity = value;
            }
        }
    };

    return (
        <div className="absolute top-4 right-4 z-50 max-h-screen overflow-y-auto">
            {!hideAdminPanel && 
            <div className="bg-white-default p-4 rounded-lg shadow-lg border border-gray-200">
                <div className="flex gap-2 justify-between items-center mb-2">
                    <label className="text-sm font-semibold text-gray-800">
                        Admin Controls
                    </label>
                    <button 
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 
                        hover:bg-gray-200 transition-colors duration-200 text-gray-600 hover:text-gray-800
                        focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                        <span className="text-lg leading-none">
                            {isExpanded ? '−' : '+'}
                        </span>
                    </button>
                </div>

                {!isExpanded && (galleryFileName || carFileName) && (
                    <div className="text-xs text-gray-600">
                        {galleryFileName && <div>Gallery: {galleryFileName}</div>}
                        {carFileName && <div>Car: {carFileName}</div>}
                    </div>
                )}

                {isExpanded && (
                    <div className="space-y-4">
                        {/* realtime camera position */}
                        <div className="border-b pb-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Camera Information</h3>
                            <div className="space-y-2 text-xs">
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <span className="text-gray-600">Position X:</span>
                                        <span className="ml-1">{cameraPosition.x}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Y:</span>
                                        <span className="ml-1">{cameraPosition.y}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Z:</span>
                                        <span className="ml-1">{cameraPosition.z}</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <span className="text-gray-600">Rotation X:</span>
                                        <span className="ml-1">{cameraRotation.x}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Y:</span>
                                        <span className="ml-1">{cameraRotation.y}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Z:</span>
                                        <span className="ml-1">{cameraRotation.z}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Reset Button */}
                        {!original && 
                        <div className="flex justify-end">
                            <button
                                onClick={handleReset}
                                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 
                                         text-gray-700 rounded transition-colors duration-200
                                         focus:outline-none focus:ring-2 focus:ring-gray-300"
                            >
                                Reset to Defaults
                            </button>
                        </div>}

                        {/* File Upload Sections */}
                        {!bgIdFromParams && (
                            <div className={`${original ? "hidden": ""}`}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Background Model
                                </label>
                                <input
                                    type="file"
                                    accept=".glb,.gltf"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) handleGalleryFileChange(file);
                                    }}
                                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 
                                    file:rounded-full file:border-0 file:text-sm file:font-semibold
                                    file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200
                                    cursor-pointer border rounded-md p-2"
                                />
                                {galleryFileName && (
                                    <div className="mt-1 text-xs text-gray-600">
                                        Selected: {galleryFileName}
                                    </div>
                                )}
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Car Model
                            </label>
                            <input
                                type="file"
                                accept=".ply"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) handleCarFileChange(file);
                                }}
                                className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 
                                file:rounded-full file:border-0 file:text-sm file:font-semibold
                                file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200
                                cursor-pointer border rounded-md p-2"
                            />
                            {carFileName && (
                                <div className="mt-1 text-xs text-gray-600">
                                    Selected: {carFileName}
                                </div>
                            )}
                        </div>

                        {/* Gallery Toggle */}
                        {/* <div className="border-t pt-4">
                            <div className="flex items-center justify-between">
                                <label className="text-sm text-gray-600">Toggle Environment</label>
                                <input
                                    type="checkbox"
                                    checked={galleryEnabled}
                                    onChange={handleGalleryToggle}
                                    className="rounded"
                                />
                            </div>
                        </div> */}

                        {/* Light Controls */}
                        {!original &&
                        <div className="border-t pt-4">
                            <h3 className="font-semibold mb-2 text-blue-500 text-xl">Light Controls</h3>
                            
                            {/* Directional Light */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="font-semibold mb-2">Directional Light</label>
                                    <input
                                        type="checkbox"
                                        checked={directionalEnabled}
                                        onChange={handleDirectionalToggle}
                                        className="rounded"
                                    />
                                </div>
                                {directionalEnabled && (
                                    <div className="space-y-4 pl-4">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm text-gray-600">Cast Shadows</label>
                                            <input
                                                type="checkbox"
                                                checked={directionalShadowsEnabled}
                                                onChange={handleDirectionalShadowsToggle}
                                                className="rounded"
                                            />
                                        </div>
                                        <NumberInput
                                            label="Intensity"
                                            value={directionalIntensity}
                                            onChange={handleDirectionalIntensityChange}
                                            min={0}
                                            max={10}
                                        />
                                        <NumberInput
                                            label="Shadow Intensity"
                                            value={directionalShadowIntensity}
                                            onChange={handleDirectionalShadowIntensityChange}
                                            min={0}
                                            max={10}
                                            step={0.1}
                                        />
                                        <div className="space-y-2">
                                            <label className="text-sm text-gray-600">Euler Angles</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                <NumberInput
                                                    label="X"
                                                    value={directionalEulerX}
                                                    onChange={(e) => handleDirectionalEulerChange('x', e)}
                                                    min={-180}
                                                    max={180}
                                                    step={1}
                                                />
                                                <NumberInput
                                                    label="Y"
                                                    value={directionalEulerY}
                                                    onChange={(e) => handleDirectionalEulerChange('y', e)}
                                                    min={-180}
                                                    max={180}
                                                    step={1}
                                                />
                                                <NumberInput
                                                    label="Z"
                                                    value={directionalEulerZ}
                                                    onChange={(e) => handleDirectionalEulerChange('z', e)}
                                                    min={-180}
                                                    max={180}
                                                    step={1}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Point Light */}
                            <div className="space-y-3 mt-4">
                                <div className="flex items-center justify-between">
                                    <label className="font-semibold mb-2">Point Light</label>
                                    <input
                                        type="checkbox"
                                        checked={pointEnabled}
                                        onChange={handlePointToggle}
                                        className="rounded"
                                    />
                                </div>
                                {pointEnabled && (
                                    <div className="space-y-4 pl-4">
                                        <NumberInput
                                            label="Intensity"
                                            value={pointIntensity}
                                            onChange={handlePointIntensityChange}
                                            min={0}
                                            max={10}
                                        />
                                        <NumberInput
                                            label="Range"
                                            value={pointRange}
                                            onChange={handlePointRangeChange}
                                            min={0}
                                            max={200}
                                            step={1}
                                        />
                                        <div className="space-y-2">
                                            <label className="text-sm text-gray-600">Position</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                <NumberInput
                                                    label="X"
                                                    value={pointPositionX}
                                                    onChange={(e) => handlePointPositionChange('x', e)}
                                                    min={-50}
                                                    max={50}
                                                    step={1}
                                                />
                                                <NumberInput
                                                    label="Y"
                                                    value={pointPositionY}
                                                    onChange={(e) => handlePointPositionChange('y', e)}
                                                    min={-50}
                                                    max={50}
                                                    step={1}
                                                />
                                                <NumberInput
                                                    label="Z"
                                                    value={pointPositionZ}
                                                    onChange={(e) => handlePointPositionChange('z', e)}
                                                    min={-50}
                                                    max={50}
                                                    step={1}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Ambient Light */}
                            <div className="mt-4">
                                <div className="flex items-center justify-between">
                                    <label className="font-semibold mb-2">Ambient Light</label>
                                    <input
                                        type="checkbox"
                                        checked={ambientEnabled}
                                        onChange={handleAmbientToggle}
                                        className="rounded"
                                    />
                                </div>
                            </div>
                        </div>}

                        {/* Car and Shadow Caster Position Controls */}
                        {!original && 
                        <>
                        <div className="border-t pt-4 space-y-4">
                            <h3 className="font-semibold mb-2 text-blue-500 text-xl">Translations</h3>
                            <div className="flex space-x-4">
                                <div className="space-y-2">
                                    <h4 className="font-semibold mb-2">Car Position</h4>
                                    <NumberInput
                                        label="X"
                                        value={carPositionX}
                                        onChange={(e) => handleCarPositionChange('x', e)}
                                        min={-50}
                                        max={50}
                                        step={0.1}
                                    />
                                    <NumberInput
                                        label="Y"
                                        value={carPositionY}
                                        onChange={(e) => handleCarPositionChange('y', e)}
                                        min={-50}
                                        max={50}
                                        step={0.1}
                                    />
                                    <NumberInput
                                        label="Z"
                                        value={carPositionZ}
                                        onChange={(e) => handleCarPositionChange('z', e)}
                                        min={-50}
                                        max={50}
                                        step={0.1}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <h4 className="font-semibold mb-2">Shadow Caster Position</h4>
                                    <NumberInput
                                        label="X"
                                        value={shadowPositionX}
                                        onChange={(e) => handleShadowPositionChange('x', e)}
                                        min={-50}
                                        max={50}
                                        step={0.1}
                                    />
                                    <NumberInput
                                        label="Y"
                                        value={shadowPositionY}
                                        onChange={(e) => handleShadowPositionChange('y', e)}
                                        min={-50}
                                        max={50}
                                        step={0.1}
                                    />
                                    <NumberInput
                                        label="Z"
                                        value={shadowPositionZ}
                                        onChange={(e) => handleShadowPositionChange('z', e)}
                                        min={-50}
                                        max={50}
                                        step={0.1}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Car and Shadow Caster Rotation Controls */}
                        <div className="border-t pt-4 space-y-4">
                            <h3 className="font-semibold mb-2">Rotations</h3>
                            <div className="flex space-x-4">
                                <div className="space-y-2">
                                    <h4 className="font-semibold mb-2">Car Rotation</h4>
                                    <NumberInput
                                        label="X"
                                        value={carEulerX}
                                        onChange={(e) => handleCarEulerChange('x', e)}
                                        min={-360}
                                        max={360}
                                        step={1}
                                    />
                                    <NumberInput
                                        label="Y"
                                        value={carEulerY}
                                        onChange={(e) => handleCarEulerChange('y', e)}
                                        min={-360}
                                        max={360}
                                        step={1}
                                    />
                                    <NumberInput
                                        label="Z"
                                        value={carEulerZ}
                                        onChange={(e) => handleCarEulerChange('z', e)}
                                        min={-360}
                                        max={360}
                                        step={1}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <h4 className="font-semibold mb-2">Shadow Caster Rotation</h4>
                                    <NumberInput
                                        label="X"
                                        value={shadowEulerX}
                                        onChange={(e) => handleShadowEulerChange('x', e)}
                                        min={-360}
                                        max={360}
                                        step={1}
                                    />
                                    <NumberInput
                                        label="Y"
                                        value={shadowEulerY}
                                        onChange={(e) => handleShadowEulerChange('y', e)}
                                        min={-360}
                                        max={360}
                                        step={1}
                                    />
                                    <NumberInput
                                        label="Z"
                                        value={shadowEulerZ}
                                        onChange={(e) => handleShadowEulerChange('z', e)}
                                        min={-360}
                                        max={360}
                                        step={1}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Car and Shadow Caster Scale Controls */}
                        <div className="border-t pt-4 pb-4 space-y-4">
                            <h3 className="font-semibold mb-2">Scales</h3>
                            <div className="flex space-x-4">
                                <div className="space-y-2">
                                    <h4 className="font-semibold mb-2">Car Scale</h4>
                                    <NumberInput
                                        label="X"
                                        value={carScaleX}
                                        onChange={(e) => handleCarScaleChange('x', e)}
                                        min={0.1}
                                        max={50}
                                        step={0.1}
                                    />
                                    <NumberInput
                                        label="Y"
                                        value={carScaleY}
                                        onChange={(e) => handleCarScaleChange('y', e)}
                                        min={0.1}
                                        max={50}
                                        step={0.1}
                                    />
                                    <NumberInput
                                        label="Z"
                                        value={carScaleZ}
                                        onChange={(e) => handleCarScaleChange('z', e)}
                                        min={0.1}
                                        max={50}
                                        step={0.1}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <h4 className="font-semibold mb-2">Shadow Caster Scale</h4>
                                    <NumberInput
                                        label="X"
                                        value={shadowScaleX}
                                        onChange={(e) => handleShadowScaleChange('x', e)}
                                        min={0.1}
                                        max={50}
                                        step={0.1}
                                    />
                                    <NumberInput
                                        label="Y"
                                        value={shadowScaleY}
                                        onChange={(e) => handleShadowScaleChange('y', e)}
                                        min={0.1}
                                        max={50}
                                        step={0.1}
                                    />
                                    <NumberInput
                                        label="Z"
                                        value={shadowScaleZ}
                                        onChange={(e) => handleShadowScaleChange('z', e)}
                                        min={0.1}
                                        max={50}
                                        step={0.1}
                                    />
                                </div>
                            </div>
                        </div>
                        </>}

                        {/* Save Configuration Form */}
                        {(galleryFileName || bgIdFromParams )&& 
                        <div className="border-t pt-4 pb-4 space-y-4">
                            <h3 className="font-semibold mb-2 text-blue-500 text-xl">
                                {bgIdFromParams ? 'Update Configuration' : 'Save Configuration'}
                            </h3>
                                {!bgIdFromParams &&
                                    <>
                                        <input
                                            type="text"
                                            placeholder="Name"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            className="block w-full text-sm text-gray-700 border rounded-md p-2"
                                        />
                                        <select
                                            value={type}
                                            onChange={(e) => setType(e.target.value)}
                                            className="block w-full text-sm text-gray-700 border rounded-md p-2"
                                        >
                                            <option value="">Select Type</option>
                                            <option value="outdoor">Outdoor</option>
                                            <option value="turntable">Turntable</option>
                                            <option value="showroom">Showroom</option>
                                        </select>
                                    </>
                                }
                            <button
                                onClick={bgIdFromParams ? () => updateBgConfig(bgIdFromParams) : saveBgConfig}
                                disabled={isSaving}
                                className={`w-full px-4 py-2 text-sm ${isSaving
                                        ? 'bg-gray-400 cursor-not-allowed text-neutral-950'
                                        : 'bg-blue-500 hover:bg-blue-600 text-slate-50'
                                    } text-white rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 flex items-center justify-center gap-2`}>
                                {isSaving && (
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                {isSaving
                                    ? (bgIdFromParams ? 'Updating...' : 'Saving...')
                                    : (bgIdFromParams ? 'Update Configuration' : 'Save Changes')
                                }
                            </button>
                        </div>}
                    </div>
                )}
            </div>}
        </div>
    );
};

export default AdminPanel;